import React, { useState } from "react";
import { toast } from "sonner";
import {
  MapPin,
  Home,
  Building2,
  Plus,
  Pencil,
  Trash2,
  Star,
  ShieldCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const LS_KEY = "dyntra_addresses_v1";

const seed = [
  {
    id: "a1",
    type: "Home",
    name: "Ananya Sharma",
    line1: "123, Green Avenue, Park Street",
    line2: "",
    city: "Bangalore",
    state: "Karnataka",
    pincode: "560001",
    country: "India",
    phone: "+91 98765 43210",
    is_default: true,
  },
  {
    id: "a2",
    type: "Clinic",
    name: "CityCare Pharmacy",
    line1: "45, Brigade Road",
    line2: "New Orion Mall",
    city: "Bangalore",
    state: "Karnataka",
    pincode: "560025",
    country: "India",
    phone: "+91 91234 56789",
    is_default: false,
  },
  {
    id: "a3",
    type: "Home",
    name: "Ananya Sharma",
    line1: "8th Cross, Koramangala 4th Block",
    line2: "",
    city: "Bangalore",
    state: "Karnataka",
    pincode: "560034",
    country: "India",
    phone: "+91 99887 76655",
    is_default: false,
  },
];

function readAll() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return seed;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : seed;
  } catch {
    return seed;
  }
}

function writeAll(list) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {}
}

const emptyAddress = {
  id: "",
  type: "Home",
  name: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  phone: "",
  is_default: false,
};

export default function Addresses() {
  const [list, setList] = useState(readAll);
  const [dialog, setDialog] = useState({ open: false, data: emptyAddress });

  const persist = (next) => {
    setList(next);
    writeAll(next);
  };

  const defaultCount = list.filter((a) => a.is_default).length;
  const otherCount = list.length - defaultCount;

  const openAdd = () => setDialog({ open: true, data: { ...emptyAddress, id: `a${Date.now()}` } });
  const openEdit = (a) => setDialog({ open: true, data: a });
  const close = () => setDialog({ open: false, data: emptyAddress });

  const save = () => {
    const d = dialog.data;
    if (!d.name.trim() || !d.line1.trim() || !d.city.trim() || !d.pincode.trim()) {
      toast.error("Please fill required fields");
      return;
    }
    const exists = list.some((a) => a.id === d.id);
    let next = exists ? list.map((a) => (a.id === d.id ? d : a)) : [...list, d];
    if (d.is_default) next = next.map((a) => ({ ...a, is_default: a.id === d.id }));
    persist(next);
    toast.success(exists ? "Address updated" : "Address added");
    close();
  };

  const remove = (id) => {
    persist(list.filter((a) => a.id !== id));
    toast.success("Address deleted");
  };

  const makeDefault = (id) => {
    persist(list.map((a) => ({ ...a, is_default: a.id === id })));
    toast.success("Default address updated");
  };

  return (
    <div data-testid="account-addresses" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Manage Address
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Add, edit or remove your delivery addresses.
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={MapPin} label="Saved Addresses" value={list.length} />
        <Kpi icon={Star} label="Default Address" value={defaultCount} />
        <Kpi icon={Home} label="Other Addresses" value={otherCount} />
        <Kpi icon={ShieldCheck} label="Secure Deliveries" value="100%" />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={openAdd}
          data-testid="address-add"
          className="dyntra-btn-primary"
        >
          <Plus className="h-4 w-4" /> Add New Address
        </button>
        <div className="text-xs text-gray-500 flex items-center gap-2">
          Sort by:
          <select className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white">
            <option>Recently Added</option>
            <option>City</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((a) => (
          <div
            key={a.id}
            data-testid={`address-card-${a.id}`}
            className="rounded-2xl border border-gray-100 bg-white p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center shrink-0">
                  {a.type === "Clinic" ? (
                    <Building2 className="h-5 w-5 text-[#4B2AAD]" />
                  ) : (
                    <Home className="h-5 w-5 text-[#4B2AAD]" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    {a.type}
                    {a.is_default && (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 border border-emerald-100">
                        ✓ Default
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-900 mt-1">{a.name}</p>
                  <p className="text-xs text-gray-500 leading-relaxed mt-1">
                    {a.line1}
                    {a.line2 ? `, ${a.line2}` : ""}
                    <br />
                    {a.city}, {a.state} – {a.pincode}
                    <br />
                    {a.country}
                    <br />
                    Phone: {a.phone}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5 text-xs">
                <button
                  onClick={() => openEdit(a)}
                  data-testid={`address-edit-${a.id}`}
                  className="inline-flex items-center gap-1 text-[#4B2AAD] hover:underline font-semibold"
                >
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button
                  onClick={() => remove(a.id)}
                  className="inline-flex items-center gap-1 text-red-500 hover:underline font-semibold"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
                {!a.is_default && (
                  <button
                    onClick={() => makeDefault(a.id)}
                    data-testid={`address-default-${a.id}`}
                    className="inline-flex items-center gap-1 text-gray-500 hover:text-[#4B2AAD] font-semibold"
                  >
                    <Star className="h-3 w-3" /> Set as Default
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-4 text-xs text-gray-700">
        <b>Tip:</b> Set a default address to speed up checkout and ensure hassle-free deliveries.
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => (o ? null : close())}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {list.some((a) => a.id === dialog.data.id) ? "Edit Address" : "Add New Address"}
            </DialogTitle>
          </DialogHeader>
          <AddressForm
            data={dialog.data}
            onChange={(next) => setDialog((s) => ({ ...s, data: next }))}
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button className="dyntra-btn-ghost" onClick={close}>
              Cancel
            </button>
            <button className="dyntra-btn-primary" onClick={save} data-testid="address-save">
              Save Address
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
        <Icon className="h-4 w-4 text-[#4B2AAD]" />
      </div>
      <div>
        <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
        <p className="text-[11px] text-gray-500">{label}</p>
      </div>
    </div>
  );
}

function AddressForm({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2 flex items-center gap-3">
        <label className="text-xs font-bold uppercase text-gray-500">Type</label>
        {["Home", "Clinic", "Office", "Other"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => set("type", t)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${
              data.type === t
                ? "bg-[#F3EFFC] text-[#4B2AAD] border-[#4B2AAD]"
                : "text-gray-600 border-gray-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <TxtField label="Full Name*" value={data.name} onChange={(v) => set("name", v)} />
      <TxtField label="Phone*" value={data.phone} onChange={(v) => set("phone", v)} />
      <TxtField label="Address Line 1*" value={data.line1} onChange={(v) => set("line1", v)} full />
      <TxtField label="Address Line 2" value={data.line2} onChange={(v) => set("line2", v)} full />
      <TxtField label="City*" value={data.city} onChange={(v) => set("city", v)} />
      <TxtField label="State*" value={data.state} onChange={(v) => set("state", v)} />
      <TxtField label="Pincode*" value={data.pincode} onChange={(v) => set("pincode", v)} />
      <TxtField label="Country" value={data.country} onChange={(v) => set("country", v)} />
      <label className="sm:col-span-2 flex items-center gap-2 text-sm text-gray-700 mt-1">
        <input
          type="checkbox"
          checked={data.is_default}
          onChange={(e) => set("is_default", e.target.checked)}
          className="accent-[#4B2AAD] h-4 w-4"
        />
        Make this my default address
      </label>
    </div>
  );
}

function TxtField({ label, value, onChange, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[11px] font-semibold text-gray-500 uppercase">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
      />
    </div>
  );
}
